import React from 'react';
import { Link } from 'react-router-dom';
import { Article } from '../types';
import { Clock } from 'lucide-react';

export const ArticleCard: React.FC<{ article: Article; featured?: boolean }> = ({ article, featured }) => {
  const articleUrl = `/news/${article.category.toLowerCase()}/${article.slug || article.id}`;

  if (featured) {
    return (
      <Link to={articleUrl} className="group block mb-12">
        <div className="relative aspect-video w-full overflow-hidden rounded-xl mb-4 shadow-sm bg-gray-100">
          <img
            src={article.coverImage}
            alt={article.title}
            loading="eager" // Hero images should load immediately (LCP optimization)
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute top-4 left-4 bg-brand-500 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider rounded-sm shadow-md">
            {article.category}
          </div>
        </div>
        <h2 className="text-2xl md:text-3xl font-serif font-bold text-brand-900 leading-tight mb-2 group-hover:text-brand-400 transition-colors">
          {article.title}
        </h2>
        <p className="text-base text-gray-600 line-clamp-2 md:w-3/4 mb-4 font-serif">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 text-sm text-gray-500 font-sans">
          <span className="font-bold text-brand-900">{article.author.name}</span>
          <span>•</span>
          <span className="flex items-center gap-1"><Clock size={14} /> {article.readTime} min read</span>
        </div>
      </Link>
    );
  }

  return (
    <Link to={articleUrl} className="group flex gap-4 md:gap-6 items-start mb-8 last:mb-0">
      <div className="w-1/3 aspect-[4/3] md:aspect-video rounded-lg overflow-hidden flex-shrink-0 shadow-sm bg-gray-100">
        {article.coverImage ? (
          <img
            src={article.coverImage}
            alt={article.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gray-200" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-xs font-bold text-brand-500 uppercase tracking-wider mb-1 block">{article.category}</span>
        <h3 className="text-base md:text-lg font-serif font-bold text-brand-900 leading-snug mb-1 group-hover:text-brand-400 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="hidden md:block text-gray-600 text-xs line-clamp-2 mb-2 font-serif">
          {article.excerpt}
        </p>
        <div className="text-xs text-gray-400 flex items-center gap-2 font-sans">
          <span>{article.author?.name || 'Nida Staff'}</span>
          <span>•</span>
          <span>{article.publishedAt ? new Date(article.publishedAt).toLocaleDateString() : 'Draft'}</span>
        </div>
      </div>
    </Link>
  );
};